"use client";

import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
} from "@tanstack/react-query";
import {
  type ActivitySessionLikeListResponse,
  listActivitySessionLikes,
  toggleActivitySessionLike,
} from "@/actions/activity-session-interaction";

type LikesPage = ActivitySessionLikeListResponse;

export const activitySessionLikesKeys = {
  likes: (activitySessionId: number) =>
    ["activitySessions", "likes", activitySessionId] as const,
};

export function useActivitySessionLikes(
  activitySessionId: number | null,
  options?: {
    enabled?: boolean;
    limit?: number;
  },
) {
  const { enabled = true, limit = 30 } = options ?? {};

  return useInfiniteQuery({
    queryKey: activitySessionLikesKeys.likes(activitySessionId ?? 0),
    enabled: Boolean(activitySessionId && enabled),
    queryFn: async ({ pageParam }) => {
      if (!activitySessionId) {
        return {
          likes: [],
          hasMore: false,
          nextCursor: null,
        } satisfies LikesPage;
      }

      const result = await listActivitySessionLikes({
        activitySessionId,
        cursor: pageParam ?? undefined,
        limit,
      });

      if (!result.ok) {
        throw new Error(result.message ?? "Failed to fetch likes");
      }

      return result.data;
    },
    initialPageParam: null as LikesPage["nextCursor"],
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.nextCursor : undefined,
    staleTime: 15_000,
    refetchOnMount: false,
    placeholderData: keepPreviousData,
  });
}

type ToggleActivitySessionLikeVariables = {
  activitySessionId: number;
  currentlyLiked: boolean;
};

export function useToggleActivitySessionLikeMutation() {
  return useMutation({
    mutationKey: ["activitySessions", "toggle-like"],
    mutationFn: async ({
      activitySessionId,
    }: ToggleActivitySessionLikeVariables) => {
      const result = await toggleActivitySessionLike({ activitySessionId });
      if (!result.ok) {
        throw new Error(result.message ?? "Failed to toggle like");
      }
      return { liked: result.data.liked };
    },
    onMutate: async ({
      activitySessionId,
      currentlyLiked,
    }: ToggleActivitySessionLikeVariables) => {
      // We don't have a feed cache for activity sessions yet like regular posts,
      // so we just track the previous state for potential rollback
      return { activitySessionId, previousLiked: currentlyLiked };
    },
    onError: (_err, _vars, _ctx) => {
      // Rollback is handled by the component via the error callback
    },
  });
}
